<template>
  <div class="qa-item-thread-add-answer">
    <div class="qa-item-thread-add-answer__title mb-4 text-text-primary">
      {{ $t("project.question-answer.QuestionAnswerItemThreadAnswerAdd.title") }}
    </div>

    <VisualTextEditor
      ref="answerRichTextField"
      v-model:value="newAnswer"
      :taggable-groups="taggableGroups"
      :taggable-members="taggableParticipants"
      :mention-list-max-visible-items="5"
      :prevent-duplicate-mentions="true"
      qa-variant
    >
      <template #inside-footer>
        <div class="qa-editor-footer">
          <DocumentsPickerSelectedChips
            v-if="allLinkedDocuments.length > 0"
            :selected="allLinkedDocuments"
            @remove="removeLinkedDocument"
          />

          <div class="qa-editor-footer__actions">
            <input
              ref="fileInput"
              class="d-none"
              type="file"
              accept=".pdf, .doc, .docx, .xls, .xlsx"
              multiple
              @change="onUploadedFilesPicked($event.target.files)"
            />

            <AppMenu
              :disabled="postAnswerPending"
              :menu="attachmentMenuItems"
              data-locator="qa-answer-attachments-menu"
            >
              <template #activator="{ props: menuProps }">
                <AppTooltip :disabled="postAnswerPending">
                  <template #activator="{ props: tooltipProps }">
                    <app-button
                      type="icon"
                      size="small"
                      v-bind="{ ...menuProps, ...tooltipProps }"
                      class="blur-editor-exception"
                      data-locator="qa-answer-attachments-menu-button"
                    >
                      <app-material-icon name="attach_file" />
                    </app-button>
                  </template>

                  {{ $t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.attachment.tooltip') }}
                </AppTooltip>
              </template>
            </AppMenu>

            <AppTooltip :disabled="postAnswerPending || taggableParticipants.length === 0">
              <template #activator="{ props }">
                <app-button
                  type="icon"
                  size="small"
                  v-bind="props"
                  class="blur-editor-exception"
                  :disabled="postAnswerPending || taggableParticipants.length === 0"
                  data-locator="qa-answer-tag-button"
                  @click="onOpenMentionList"
                >
                  <app-material-icon name="alternate_email" />
                </app-button>
              </template>

              {{ $t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.tag.tooltip') }}
            </AppTooltip>

            <div class="ml-auto">
              <app-button
                type="primary"
                variant="brand"
                size="small"
                :disabled="isSubmitButtonDisabled"
                :loading="postAnswerPending"
                class="blur-editor-exception"
                data-locator="qa-answer-submit"
                @click="preparePostAnswer"
              >
                {{ $t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.submit') }}
              </app-button>
            </div>
          </div>
        </div>
      </template>
    </VisualTextEditor>

    <QuestionAnswerAddDocumentsDialog
      :is-open="isDialogOpen"
      :model-value="selectedDocuments"
      @update:isOpen="isDialogOpen = $event"
      @update:modelValue="selectedDocuments = $event"
      @confirm="onDocumentsPicked"
    />

    <IManageFilePickerDialog
      v-if="imanageEnabledAndLogged"
      v-model:is-open="isIManageDialogOpen"
      @files="onIManage"
    />

    <PolyOfficeDialog
      v-if="polyOfficeEnabled && polyOfficeDialogIsOpen"
      @callback="polyOfficeDialogCallback"
    />

    <AppWarningSnackbar
      v-model="showWatermarkWarning"
      :title="$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.watermarkWarning.title')"
      :text="$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.watermarkWarning.text')"
      data-locator="watermark-applied-warning"
      @dont-show-again="onWatermarkWarningDontShowAgain"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DocumentsPickerSelectedChips from '@/common/documents-picker/DocumentsPickerSelectedChips.vue'
import IManageFilePickerDialog from '@/common/imanage/IManageFilePickerDialog.vue'
import { track, USER_ROLE } from '@/common/pendo/agent'
import { DocumentsImport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import { isWarningDismissed, dismissWarning } from '@/common/utils/warningDontShowAgain'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import AppWarningSnackbar from '@/common/warning-snackbar/AppWarningSnackbar.vue'
import { FileToUpload } from '@/models/documentToUpload.model.js'
import QuestionAnswerAddDocumentsDialog from '@/project/question-answer/dialogs/QuestionAnswerAddDocumentsDialog.vue'
import documentsService from '@/services/documents.service.js'
import service from '@/services/questionAnswer.service'
import { POST_FILDERS_FROM_IMANAGE } from '@/store/modules/documents/action_types'
import { GET_ACTION_INFO, GET_IMPORT_IFRAME } from '@/store/modules/poly-office/action_types'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'
import { QuestionAnswerMutationTypes } from '@/store/modules/question-answer/mutation_types'

const WATERMARK_WARNING_SETTING_KEY_PREFIX = 'hide-qa-watermark-warning'

export default {
  name: 'QuestionAnswerItemThreadAnswerAdd',
  components: {
    AppTooltip,
    AppMenu,
    AppWarningSnackbar,
    VisualTextEditor,
    QuestionAnswerAddDocumentsDialog,
    DocumentsPickerSelectedChips,
    IManageFilePickerDialog,
    PolyOfficeDialog,
  },
  props: {
    mnemo: { type: String, required: true },
  },
  data () {
    return {
      newAnswer: '',
      isDialogOpen: false,
      isIManageDialogOpen: false,
      selectedDocuments: [],
      uploadedDocuments: [],
      lastAttachmentSource: 'none',
      showWatermarkWarning: false,
      watermarkWarningAutoHideTimeout: null,
      watermarkWarningShownForThisAction: false,
    }
  },
  computed: {
    ...mapState('questionAnswer', [
      'question',
      'postAnswerPending',
      'questionParticipants',
    ]),
    ...mapState('room', ['currentRoom']),
    ...mapState('polyOffice', {
      polyOfficeDialogIsOpen: 'dialogIsOpen',
    }),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('user', ['polyOfficeEnabled', 'currentUserId']),
    newAnswerLength () {
      return escapeHtml(this.newAnswer).length
    },
    isWatermarkEnabled () {
      return Boolean(this.currentRoom?.isDataroom && this.currentRoom?.watermarkEnabled)
    },
    watermarkWarningSettingKey () {
      return `${WATERMARK_WARNING_SETTING_KEY_PREFIX}-${this.currentRoom?.id}`
    },
    hasLinkedDocuments () {
      return this.allLinkedDocuments.length > 0
    },
    hasSomeFileUploadPending () {
      return this.uploadedDocuments.some((document) => document.status === 'pending')
    },
    isSubmitButtonDisabled () {
      return (!this.newAnswerLength && !this.hasLinkedDocuments) || this.hasSomeFileUploadPending
    },
    allLinkedDocuments () {
      return [...this.uploadedDocuments, ...this.selectedDocuments]
    },
    attachmentMenuItems () {
      return [
        {
          id: 'upload',
          icon: 'upload',
          title: this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.attachment.uploadFile'),
          locatorValue: 'qa-attach-upload',
          disabled: this.postAnswerPending,
          action: () => this.openFilePicker(),
        },
        {
          id: 'documents',
          icon: 'folder_open',
          title: this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.attachment.fromDocuments'),
          locatorValue: 'qa-attach-documents',
          disabled: this.postAnswerPending,
          action: () => { this.isDialogOpen = true },
        },

        ...(this.polyOfficeEnabled
          ? [{
            id: 'lexis-poly',
            iconComponent: 'app-icon-poly-office',
            title: this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.attachment.lexisPoly'),
            locatorValue: 'qa-attach-lexis-poly',
            disabled: this.postAnswerPending,
            action: () => this.onLexisPoly(),
          }]
          : []),

        ...(this.imanageEnabledAndLogged
          ? [{
            id: 'imanage',
            iconComponent: 'app-icon-imanage',
            title: this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.attachment.iManage'),
            locatorValue: 'qa-attach-imanage',
            disabled: this.postAnswerPending,
            action: () => this.isIManageDialogOpen = true,
          }]
          : []),
      ]
    },
    taggableGroups () {
      return this.questionParticipants.groups
        .filter(group => group?.id && group?.name)
        .map(group => ({
          id: group.id,
          name: group.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        }))
    },
    taggableParticipants () {
      const deduplicated = new Map()

      const author = this.questionParticipants.author
      if (author?.id) {
        deduplicated.set(author.id, author)
      }

      this.questionParticipants.users.forEach((user) => {
        if (user?.id) {
          deduplicated.set(user.id, user)
        }
      })

      return Array.from(deduplicated.values())
        .filter(user => user?.id && user?.fullName)
        .map(user => {
          const isAuthor = user.id === this.questionParticipants.author?.id

          return {
            id: user.id,
            rawFullName: user.fullName,
            fullName: isAuthor
              ? `${user.fullName} (${this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.author')})`
              : user.fullName,
            initials: user.initials || '',
            shortInitials: user.initials || '',
            isAuthor,
            groupLabel: user.userGroupName || '',
            company: user.company || '',
            email: user.email || '',
          }
        })
        .sort((a, b) => a.rawFullName.localeCompare(b.rawFullName, undefined, {
          numeric: true,
          sensitivity: 'base',
        }))
    },
  },
  beforeUnmount () {
    clearTimeout(this.watermarkWarningAutoHideTimeout)
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.POST_ANSWER]),
    ...mapActions('documents', [POST_FILDERS_FROM_IMANAGE]),
    ...mapActions('polyOffice', [GET_IMPORT_IFRAME, GET_ACTION_INFO]),
    ...mapMutations('questionAnswer', [
      QuestionAnswerMutationTypes.SET_QUESTION,
      QuestionAnswerMutationTypes.ADD_ANSWER_TO_QUESTION,
    ]),
    openFilePicker () {
      this.lastAttachmentSource = 'upload'
      this.$refs.fileInput?.click()
    },
    onOpenMentionList () {
      this.$refs.answerRichTextField?.onOpenMentionList?.()
      this.$refs.answerRichTextField?.focus?.()
    },
    async onUploadedFilesPicked (files) {
      await this.onFilesPicked(files, 'upload')
      this.$refs.fileInput.value = null
    },
    async onFilesPicked (files, source) {
      if (!this.question?.internalDirectoryId) {
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.postAnswerError'))
        throw new Error('Missing internalDirectoryId on current Q&A question')
      }
      if (files.length > 0) {
        this.watermarkWarningShownForThisAction = false
        this.maybeShowWatermarkWarning()
      }
      files.forEach(async (file) => {
        const fileToUpload = new FileToUpload(
          file,
          'document',
          this.currentRoom.dataroomInfo.qaDirectoryId,
        )

        fileToUpload.name = fileToUpload.fileObject.name
        fileToUpload.extension = fileToUpload.fileObject.extension
        fileToUpload.size = fileToUpload.fileObject.size
        this.uploadedDocuments.push(fileToUpload)

        const reactiveFile = this.uploadedDocuments.find(d => d.key === fileToUpload.key)

        try {
          if (source === 'imanage') {
            const data = {
              parentId: this.question.internalDirectoryId,
              files: [fileToUpload.fileObject.id],
            }
            const response = await this.POST_FILDERS_FROM_IMANAGE({
              mnemo: this.mnemo,
              data,
            })
            reactiveFile.id = response.data.ids[reactiveFile.fileObject.id]
            reactiveFile.status = 'success'

            return
          }
          else if (source === 'upload') {
            const uploadedFileResponse = await documentsService.postFile(reactiveFile)
            reactiveFile.id = uploadedFileResponse.data.id
            reactiveFile.size = uploadedFileResponse.data.size
            reactiveFile.basename = uploadedFileResponse.data.basename
            reactiveFile.ext = uploadedFileResponse.data.ext
            reactiveFile.status = 'success'
          }
        } catch (error) {
          console.error(error)
          reactiveFile.status = 'error'
          if (error.response?.data?.errorCode === 'ERR_STORAGE_EXCEEDED') {
            this.uploadedDocuments = this.uploadedDocuments.filter(d => d.key !== reactiveFile.key)
            toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.dataroomStorageExceededError'))
          }
          throw error
        }
      })
    },
    onDocumentsPicked (docs) {
      this.lastAttachmentSource = 'documents'
      this.watermarkWarningShownForThisAction = false
      const picked = Array.isArray(docs) ? docs : []
      this.selectedDocuments = picked.filter(d => d?.type === 'file')
      if (this.selectedDocuments.length > 0) {
        this.maybeShowWatermarkWarning()
      }
    },
    maybeShowWatermarkWarning () {
      if (!this.isWatermarkEnabled || this.watermarkWarningShownForThisAction) {
        return
      }
      if (isWarningDismissed(this.currentUserId, this.watermarkWarningSettingKey)) {
        return
      }
      this.watermarkWarningShownForThisAction = true
      this.showWatermarkWarning = true
    },
    onWatermarkWarningDontShowAgain () {
      dismissWarning(this.currentUserId, this.watermarkWarningSettingKey)
      this.showWatermarkWarning = false
    },
    removeLinkedDocument (doc) {
      if (doc.status) {
        this.uploadedDocuments = this.uploadedDocuments.filter(d => d.key !== doc.key)
        return
      }

      this.selectedDocuments = this.selectedDocuments.filter(
        d => !(d?.id === doc?.id && d?.type === doc?.type),
      )
    },
    async preparePostAnswer () {
      try {
        this.$refs.answerRichTextField?.tryResolvePendingMention?.()
        this.$refs.answerRichTextField?.removeDuplicateMentions?.()
        const selectedFileIds = this.selectedDocuments
          .filter(d => d?.type === 'file')
          .map(d => d.id)

        const uploadedFileIds = this.uploadedDocuments
          .filter(d => d.status === 'success')
          .map(d => d.id)

        const payload = {
          description: this.newAnswer,
          fileIds: [...selectedFileIds, ...uploadedFileIds],
        }

        const response = await this.POST_ANSWER({
          mnemo: this.mnemo,
          question: this.question,
          data: payload,
        })

        if (this.question.answers.length === 0) {
          const responseQuestion = await service.getQuestion(this.mnemo, this.question.id)
          this.SET_QUESTION(responseQuestion.data.question)
        } else {
          this.ADD_ANSWER_TO_QUESTION({
            questionId: this.question.id,
            answer: response.data.answer,
          })
        }

        // Attachment type based on the last attachment source
        // and whether there are any selected or uploaded files
        const attachmentType = (selectedFileIds.length > 0 || uploadedFileIds.length > 0) 
          ? this.lastAttachmentSource 
          : 'none'

        this.newAnswer = ''
        this.selectedDocuments = []
        this.uploadedDocuments = []
        this.lastAttachmentSource = 'none'
        this.$refs.answerRichTextField?.editor?.commands?.clearContent?.()
        this.watermarkWarningShownForThisAction = false

        if (this.showWatermarkWarning) {
          clearTimeout(this.watermarkWarningAutoHideTimeout)
          this.watermarkWarningAutoHideTimeout = setTimeout(() => {
            this.showWatermarkWarning = false
          }, 3000)
        }

        toast.success(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.postAnswerSuccess'))
        track('question_answered', {
          project_mnemo: this.mnemo,
          question_id: this.question.id,
          user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
          attachment: attachmentType,
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.postAnswerError'))
      }
    },
    async onLexisPoly () {
      this.lastAttachmentSource = 'poly'

      if (!this.question?.internalDirectoryId) {
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.postAnswerError'))
        return
      }

      try {
        const payload = new DocumentsImport(this.mnemo, this.question.internalDirectoryId)
        await this.GET_IMPORT_IFRAME({
          payload,
          successMessage: this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importFailure'))
      }
    },
    async polyOfficeDialogCallback (data) {
      if (data.status !== POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        return
      }

      try {
        const response = await this.GET_ACTION_INFO()

        if (response?.receiveStatus !== 'DONE') {
          toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importFailure'))
          return
        }

        this.addPolyOfficeDocuments(response.receivedFilesData)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importFailure'))
      }
    },
    // Poly imports the files server-side into the question folder
    // so they already have a file id and are deemed already uploaded
    addPolyOfficeDocuments (files) {
      const importedFiles = (files || []).filter(file => file?.id)

      if (importedFiles.length === 0) {
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importFailure'))
        return
      }

      importedFiles.forEach(file => {
        this.uploadedDocuments.push({
          type: 'file',
          id: file.id,
          key: `poly-${file.id}`,
          name: file.name || file.basename,
          basename: file.basename,
          ext: file.extension,
          size: file.size,
          status: 'success',
        })
      })

      this.watermarkWarningShownForThisAction = false
      this.maybeShowWatermarkWarning()

      toast.success(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.polyOffice.importSuccessful'))
    },
    onIManage (data) {
      this.lastAttachmentSource = 'imanage'
      if (data?.output_data?.selected) {
        const files = data.output_data.selected
        this.onFilesPicked(files, 'imanage')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.qa-item-thread-add-answer {
  &__title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 18px;
  }

  &__attach {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
}

.qa-editor-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.qa-editor-footer__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
